import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import Footer from "../../assets/images/Footer.png";
import useHttp from "../../hooks/use-http";
import Spinner from "../Spinner";
import { useTitle } from "../../hooks/useTitle";

const isOrderAlreadyCompleted = (data) => {
  if (data?.success !== false || data?.data != null) return false;

  const statusText = `${data?.message || ""} ${data?.status || ""}`;
  return /already in/i.test(statusText);
};

const AggregatorScreen = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [simulationType, setSimulationType] = useState("success");

  const { sendRequest: cancelRequest } = useHttp();
  useTitle("Aggregator");

  const confirmTransaction = (data) => {
    setLoading(false);

    if (isOrderAlreadyCompleted(data)) {
      toast.error(
        data?.message ||
        "This transaction has already been completed.",
      );
      navigate(-2);
      return;
    }

    const result = data?.data ?? data;
    const isApproved = result?.approved === true;

    navigate(
      isApproved
        ? `/sandbox-pay/aggregator-successful/${id || ""}`
        : `/sandbox-pay/aggregator-failed/${id || ""}`,
      { state: { result } },
    );
  };

  const completeTransaction = async () => {
    const transaction = {
      outcome: simulationType === "success" ? 1 : 0,
    };
    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/sandbox/checkout/aggregator/${id}/complete`,
        {
          method: "POST",
          body: JSON.stringify(transaction),
          headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true",
          },
        },
      );

      const data = await response.json();
      confirmTransaction(data);
    } catch (error) {
      setLoading(false);
      toast.error("Something went wrong. Please try again.");
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const confirmCancel = (data) => {
    window.location.href = data?.data?.returnUrl;
  };

  const handleCancel = () => {
    cancelRequest(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/transaction/cancelTransaction/${id}`,
        headers: {
          "ngrok-skip-browser-warning": "true",
          "Content-Type": "application/json",
        },
      },
      confirmCancel,
    );
  };

  const getCssVar = (name) =>
    getComputedStyle(document.documentElement).getPropertyValue(name).trim();

  const primaryColor = getCssVar("--primary") || "#02AFF3";

  const simulationOptions = [
    {
      value: "success",
      title: "Simulate Success",
      description: "Payment completes and customer is redirected successfully.",
      badge: "Approved",
      badgeClass: "bg-emerald-50 text-emerald-700 border border-emerald-100",
    },
    {
      value: "failed",
      title: "Simulate Failed",
      description: "Payment fails and the customer sees a failure response.",
      badge: "Declined",
      badgeClass: "bg-rose-50 text-rose-700 border border-rose-100",
    },
  ];

  return (
    <div className="flex flex-col items-center font-LT">
      <div className="flex items-center justify-between md:w-[562px] w-[300px] relative mt-6 px-1">
        <button
          type="button"
          onClick={handleGoBack}
          className="flex items-center gap-1 cursor-pointer text-lightBlack"
        >
          <svg
            width="24"
            height="25"
            viewBox="0 0 24 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M14.71 16.3219L10.83 12.4419L14.71 8.56187C15.1 8.17187 15.1 7.54188 14.71 7.15187C14.32 6.76188 13.69 6.76188 13.3 7.15187L8.70998 11.7419C8.31998 12.1319 8.31998 12.7619 8.70998 13.1519L13.3 17.7419C13.69 18.1319 14.32 18.1319 14.71 17.7419C15.09 17.3519 15.1 16.7119 14.71 16.3219Z"
              fill="#323232"
            />
          </svg>
          <span>Go Back</span>
        </button>

        <button
          type="button"
          onClick={handleCancel}
          className="cursor-pointer"
          aria-label="Cancel payment"
        >
          <svg
            width="36"
            height="36"
            viewBox="0 0 36 36"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#aggregator_cancel_clip)">
              <path
                d="M18 35.7096C27.7808 35.7096 35.7097 27.7807 35.7097 17.9999C35.7097 8.21916 27.7808 0.290283 18 0.290283C8.21922 0.290283 0.290344 8.21916 0.290344 17.9999C0.290344 27.7807 8.21922 35.7096 18 35.7096Z"
                fill={primaryColor}
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M16.8934 18L11.9684 13.0751C11.8258 12.9275 11.747 12.7298 11.7487 12.5246C11.7505 12.3194 11.8328 12.1231 11.9779 11.978C12.123 11.8329 12.3193 11.7506 12.5245 11.7488C12.7297 11.747 12.9274 11.8259 13.075 11.9685L18 16.8934L22.9249 11.9685C23.0725 11.8259 23.2702 11.747 23.4754 11.7488C23.6806 11.7506 23.8769 11.8329 24.022 11.978C24.1671 12.1231 24.2494 12.3194 24.2512 12.5246C24.253 12.7298 24.1741 12.9275 24.0315 13.0751L19.1066 18L24.0315 22.925C24.1741 23.0726 24.253 23.2703 24.2512 23.4755C24.2494 23.6807 24.1671 23.877 24.022 24.0221C23.8769 24.1672 23.6806 24.2495 23.4754 24.2513C23.2702 24.253 23.0725 24.1742 22.9249 24.0316L18 19.1066L13.075 24.0316C12.9274 24.1742 12.7297 24.253 12.5245 24.2513C12.3193 24.2495 12.123 24.1672 11.9779 24.0221C11.8328 23.877 11.7505 23.6807 11.7487 23.4755C11.747 23.2703 11.8258 23.0726 11.9684 22.925L16.8934 18Z"
                fill="white"
              />
            </g>
            <defs>
              <clipPath id="aggregator_cancel_clip">
                <rect width="36" height="36" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </button>
      </div>

      <div className="md:w-[562px] w-[300px] mx-auto mt-16 overflow-hidden px-1">
        {
          <>
            <div className="flex justify-between border-b pb-6">
              <p className="text-primary xl:text-xl text-lg font-bold">
                {"Aggregator Simulation"}
              </p>
              <div className="text-right">
                {/*    <p className="xl:text-2xl text-xl text-primary font-bold">
                  <span className="text-xl">{currency}</span>{" "}
                </p> */}
                {/*    {(transactionInfo?.data?.email ||
                  transactionInfo?.data?.customerEmail) && (
                  <p className="text-[17.75px] text-normalText font-normal">
                    {transactionInfo?.data?.email ||
                      transactionInfo?.data?.customerEmail}
                  </p>
                )} */}
              </div>
            </div>

            <div className="flex flex-col items-center mt-10">
              <p className="text-[18.75px] text-darkGrey text-center font-normal md:w-[419px]">
                Choose how this aggregator payment should resolve, then proceed
                to simulate the transaction.
              </p>

              <div className="mt-8 w-full md:w-[398px]">
                <label className="block text-[11px] font-bold text-[#94a3b8] mb-3 uppercase tracking-wide">
                  Select a simulation flow
                </label>
                <div className="flex flex-col gap-3">
                  {simulationOptions.map((option) => {
                    const isSelected = simulationType === option.value;

                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setSimulationType(option.value)}
                        className={`w-full flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 text-left ${isSelected
                          ? "border-primary bg-sky-50/40 shadow-sm"
                          : "border-slate-100 hover:border-slate-200 hover:bg-slate-50/50 bg-white"
                          }`}
                      >
                        <div className="flex items-center gap-4">
                          <div
                            className={`w-5 h-5 rounded-full border flex items-center justify-center transition-all ${isSelected ? "border-primary" : "border-slate-300"
                              }`}
                          >
                            {isSelected && (
                              <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                            )}
                          </div>

                          <div className="flex flex-col gap-1">
                            <span className="text-[15px] font-semibold text-slate-800">
                              {option.title}
                            </span>
                            <span className="text-xs text-slate-500">
                              {option.description}
                            </span>
                          </div>
                        </div>

                        <span
                          className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium shrink-0 ${option.badgeClass}`}
                        >
                          {option.badge}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {loading && (
                <p className="text-center text-sm text-primary animate-pulse mt-6">
                  Processing payment, please do not refresh...
                </p>
              )}

              <button
                type="button"
                onClick={completeTransaction}
                disabled={loading || !simulationType}
                className="rounded-[10px] w-full md:w-[307px] h-[61.38px] mt-10 bg-primary text-white font-bold transition-all hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              >
                {loading ? (
                  <div className="flex justify-center items-center scale-75">
                    <Spinner />
                  </div>
                ) : (
                  `Pay`
                )}
              </button>

              <p className="text-center text-[11px] text-[#94a3b8] mt-4 flex items-center justify-center gap-1">
                Sandbox simulation only
              </p>

              <div className="flex justify-center mt-10 mb-2">
                <img src={Footer} alt="footerLogo" className="w-[227px]" />
              </div>
            </div>
          </>
        }
      </div>
    </div>
  );
};

export default AggregatorScreen;
